/**
 * Speaking instead of typing, wherever someone writes prose.
 *
 * Two screens need this — the diary and a person's page — and they need it to
 * behave identically, so the behaviour lives here rather than being written
 * twice with two sets of copy. The recogniser is the browser's own (Web
 * Speech): nothing is uploaded for it, and nothing works without HTTPS.
 *
 * The rule everything here follows: dictation APPENDS. Someone who typed half
 * a thought and then started talking must not lose the half they typed, and
 * nothing enters the text until the recogniser has finalised it — until then
 * it is shown beneath the box, where they can see what is about to land.
 */
import { useEffect, useMemo, useState } from 'react'
import { Mic, MicOff } from 'lucide-react'
import { Button } from '../ui/Button'
import { useVoiceInput } from '../../hooks/useVoiceInput'

/** Join dictated speech onto what is already written, without eating it. */
export function appendSpoken(existing: string, chunk: string): string {
  if (!existing) return chunk
  // Speech arrives without a leading space and without a full stop; the
  // person can punctuate afterwards, and guessing sentence boundaries for
  // them would be a worse guess than the one they can fix in a second.
  return /\s$/.test(existing) ? existing + chunk : `${existing} ${chunk}`
}

/** The API's error codes, in words someone can act on. */
export function micTrouble(error: string): string {
  if (error === 'not-allowed' || error === 'service-not-allowed') {
    return 'Your browser is blocking the microphone. Allow it for this site and try again.'
  }
  if (error === 'audio-capture') return 'No microphone found.'
  if (error === 'network') return 'The speech service could not be reached.'
  return 'Dictation stopped unexpectedly. You can keep typing.'
}

export interface Dictation {
  isSupported: boolean
  isListening: boolean
  error: string | null
  /** What the microphone is hearing but has not committed yet. */
  heard: string
  toggle: () => void
  stop: () => void
  lang: string
  setLang: (l: string) => void
  langs: string[]
}

/**
 * Dictate into a text field.
 *
 * `langKey` is where the chosen language is remembered — per surface, because
 * someone may keep a diary in one language and write about their family in
 * another.
 */
export function useDictationInto(
  setText: (fn: (prev: string) => string) => void,
  langKey: string,
): Dictation {
  const [heard, setHeard] = useState('')
  const [lang, setLang] = useState<string>(() =>
    localStorage.getItem(langKey) || navigator.language || 'en-US')

  // The languages the PERSON has configured in their own browser. Offering a
  // list of our own choosing would be guessing at whose life this is.
  const langs = useMemo(() => {
    const all = [lang, ...(navigator.languages ?? []), navigator.language]
      .filter(Boolean) as string[]
    return [...new Set(all)]
  }, [lang])

  useEffect(() => { localStorage.setItem(langKey, lang) }, [lang, langKey])

  const voice = useVoiceInput({
    lang,
    autoRestart: true,
    onChunk: (chunk) => { setText((prev) => appendSpoken(prev, chunk)); setHeard('') },
    onInterim: (all) => setHeard(all),
    onResult: () => setHeard(''),
  })

  return {
    isSupported: voice.isSupported,
    isListening: voice.isListening,
    error: voice.error,
    heard,
    toggle: voice.toggle,
    stop: () => { voice.stop(); setHeard('') },
    lang, setLang, langs,
  }
}

/** Hidden entirely where the browser has no Web Speech (Firefox): a button
 *  that cannot work is worse than no button. */
export function MicButton({ dictation, disabled, label = 'Speak', onToggle }: {
  dictation: Dictation
  disabled?: boolean
  label?: string
  onToggle?: () => void
}) {
  if (!dictation.isSupported) return null
  return (
    <Button
      type="button"
      size="sm"
      variant={dictation.isListening ? 'default' : 'outline'}
      onClick={() => { dictation.toggle(); onToggle?.() }}
      disabled={disabled}
      aria-pressed={dictation.isListening}
      aria-label={dictation.isListening ? 'Stop dictating' : 'Dictate'}
      title={dictation.isListening ? 'Stop dictating' : 'Speak instead of typing'}
    >
      {dictation.isListening
        ? <><MicOff className="w-4 h-4 mr-2" />Stop</>
        : <><Mic className="w-4 h-4 mr-2" />{label}</>}
    </Button>
  )
}

export function LangPicker({ dictation, disabled }: {
  dictation: Dictation
  disabled?: boolean
}) {
  if (!dictation.isSupported || dictation.langs.length < 2) return null
  return (
    <select value={dictation.lang} onChange={(e) => dictation.setLang(e.target.value)}
            disabled={disabled || dictation.isListening}
            aria-label="Dictation language"
            title="Which language you are speaking"
            className="rounded border border-soft bg-surface px-2 py-1 text-xs
                       text-secondary disabled:opacity-50">
      {dictation.langs.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  )
}

/** What the microphone is hearing right now: shown, never committed, so
 *  nothing appears in the text that the person has not seen land. */
export function LiveTranscript({ dictation, idle = 'Listening — just talk.' }: {
  dictation: Dictation
  idle?: string
}) {
  if (!dictation.isListening) return null
  return (
    <div className="flex items-start gap-2 text-sm text-muted">
      <span className="relative flex h-2 w-2 mt-1.5 flex-shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500
                         opacity-60 motion-safe:animate-ping" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
      </span>
      <span className="italic">{dictation.heard || idle}</span>
    </div>
  )
}

export function MicTrouble({ dictation }: { dictation: Dictation }) {
  if (!dictation.error) return null
  return (
    <div className="text-sm text-amber-600 dark:text-amber-400">
      {micTrouble(dictation.error)}
    </div>
  )
}
